import { ChangeEvent, FC, useCallback } from "react";
import styles from "./Settings.module.scss";
import Input from "@components/ui/input";
import Modal from "../Modal";
import ModalContent from "../Modal/ModalContent";
import { useModalStore } from "@stores/unified-modal.store";
import Tooltip from "@components/ui/tooltip";
import TooltipIcon from "@components/icons/tooltip/TooltipIcon";
import { useMemeSwap } from "@stores/meme-swap.store";

const MODAL_NAME = "Settings";
const SLIPPAGE_BUTTONS = [0, 0.5, 1, 5, 10, 20, 30];
const MIN_SLIPPAGE = 0; // Allow 0 for Auto
const MAX_SLIPPAGE = 50;

const Settings: FC = () => {
    const { setSlippagePercentage, slippagePercentage } = useMemeSwap((state) => ({
        setSlippagePercentage: state.setSlippagePercentage,
        slippagePercentage: state.slippagePercentage,
    }));

    const isVisible = useModalStore((state) => state.modalStates[MODAL_NAME]);
    const closeModal = useModalStore((state) => state.closeModal);

    const handleCloseModal = useCallback(() => {
        closeModal(MODAL_NAME);
    }, [closeModal]);

    const handleSlippageChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            // Allow empty input for user typing, but don't update state yet
            if (value === "") {
                // Optionally, you could set a temporary state or handle this differently
                // For now, we just prevent updating the store with NaN
                return;
            }

            let numericValue = parseFloat(value);

            // Handle potential NaN and clamp the value
            if (isNaN(numericValue)) {
                numericValue = MIN_SLIPPAGE; // Or restore previous value
            } else if (numericValue < MIN_SLIPPAGE) {
                // numericValue = MIN_SLIPPAGE; // Prevent negative input directly
                return; // Or allow typing but clamp on blur/save
            } else if (numericValue > MAX_SLIPPAGE) {
                numericValue = MAX_SLIPPAGE; // Clamp to max
            }

            setSlippagePercentage(numericValue);
        },
        [setSlippagePercentage],
    );

    const handleSlippageButtonClick = useCallback(
        (value: number) => {
            setSlippagePercentage(value);
        },
        [setSlippagePercentage],
    );

    return (
        <Modal isVisible={isVisible} setIsVisible={handleCloseModal}>
            <ModalContent classNames={styles.content}>
                {/* Using a div instead of form as we don't submit, state updates live */}
                <div>
                    <div className={styles.header}>
                        <h2 className={styles.headline}>Settings</h2>
                        <button className={styles.close} type="button" onClick={handleCloseModal}>
                            {/* Removed picture element for simplicity, assuming SVG handles scaling well */}
                            <img
                                height={12}
                                width={12}
                                src="/images/modal/close.svg"
                                style={{ opacity: 0.4 }}
                                alt="Close modal"
                            />
                        </button>
                    </div>
                    <div className={styles.inputHolder}>
                        <span className={styles.labelInner}>
                            <span>Slippage Tolerance</span> {/* Slightly clearer label */}
                            <div className={styles.tooltip}>
                                <a className="tooltipSlippage">
                                    <TooltipIcon type="info" />
                                </a>
                                <Tooltip anchorSelect=".tooltipSlippage">
                                    <span>
                                        The maximum allowed difference between the expected price and execution price.
                                    </span>
                                    <br />
                                    <span className="red">
                                        Higher slippage increases the risk of front-running.
                                    </span>{" "}
                                    {/* More specific warning */}
                                </Tooltip>
                            </div>
                        </span>

                        <Input
                            max={MAX_SLIPPAGE}
                            min={MIN_SLIPPAGE}
                            onChange={handleSlippageChange}
                            // Prevent negative numbers directly in input type if browser supports it well
                            onKeyDown={(e) => {
                                if (e.key === "-") e.preventDefault();
                            }}
                            value={slippagePercentage.toString()} // Keep controlled
                            name="slippage"
                            placeholder="Custom %"
                            type="number"
                            step="0.1" // Allow finer adjustments
                        />
                        <div className={styles.buttons}>
                            {SLIPPAGE_BUTTONS.map((buttonValue) => (
                                <button
                                    onClick={() => handleSlippageButtonClick(buttonValue)}
                                    key={buttonValue}
                                    type="button"
                                    // Add an 'active' class if the button's value matches the current slippage
                                    className={`${styles.button} ${slippagePercentage === buttonValue ? styles.active : ""}`}
                                >
                                    {buttonValue === 0 ? "Auto" : `${buttonValue}%`}
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Changed "Save" to "Done" as changes are live */}
                    <button type="button" onClick={handleCloseModal} className={styles.save}>
                        Done
                    </button>
                </div>
            </ModalContent>
        </Modal>
    );
};

export default Settings;
